/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file initialises the react application and service worker.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

import 'core-js';

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';

/**
 * Required to render the application to HTML DOM
 */
import * as ReactDOM from 'react-dom';

/**
 * Used to connect the redux store to the react app.
 */
import { Provider } from 'react-redux'

/**
 * Used to handle routing around the react application.
 */
import { BrowserRouter, Route, Switch, Redirect } from 'react-router-dom';

/**
 * used to parse query string values to an object.
 */
import { parse as parseQueryString } from 'qs';


/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/**
 * Application entry component.
 */
import App from './App';

/**
 * Application service worker.
 */
import * as serviceWorker from './registerServiceWorker';

/**
 * Application service worker.
 */
import createReduxStore from './store/createReduxStore';

/**
 * Context used to allow access to the theme manager further down the react chain.
 */
import ThemeManagerContext from './contexts/ThemeManagerContext';

/**
 * Context used to allow access to the message manager further down the react chain.
 */
import MessageManagerContext from './contexts/MessageManagerContext';

/**
 * Component to provide the theme to the rest of the application.
 */
import ThemeProvider from './components/ThemeProvider';

/**
 * Utility to manage the theme of the app.
 */
import ThemeManager, { createThemeLoader } from './style/ThemeManager';

/**
 * Utility to manage the messaging to the parent window of the app.
 */
import MessageManager from './utils/MessageManager';

/**
 * Default theme options.
 */
import defaultThemeOptions from './style/theme/bctTheme';

/**
 * Application error view.
 */
import Errors from './Errors';

import MessageProvider from './components/MessageProvider';
import { AppActions } from './store/reducers/app';


/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */

const themeManager = new ThemeManager({
    initialThemeOptions: defaultThemeOptions,
    themeLoaders: {
        random: createThemeLoader({
            load: () => import('./style/theme/randomTheme'),
            selector: (randomThemeModule) => randomThemeModule.default()
        })
    }
});

const store = createReduxStore();

const getQueryStringValues = (): Record<string, string> => {
    const qsValues: Record<string, string> = parseQueryString(location.search, { ignoreQueryPrefix: true }) as Record<string, string>;

    if (qsValues && Object.keys(qsValues).length !== 0) {
        try {
            sessionStorage.setItem('qs-values', JSON.stringify(qsValues));
        }
        catch (error) {
            // no seesion storage
        }

        return qsValues;
    }
    else {
        try {
            const valueJson = sessionStorage.getItem('qs-values');

            if (valueJson) {
                return JSON.parse(valueJson);
            }
        }
        catch (error) {
            // no seesion storage
        }
    }

    return {};
};

const queryStringValues = getQueryStringValues();

const messageManager = new MessageManager({
    instanceId: queryStringValues.instanceId,
    parentOrigin: queryStringValues.origin
});

const initialiseAppState = () => {
    const specificationTermId = queryStringValues.specificationTermId ? parseInt(queryStringValues.specificationTermId, 10) : null;
    const targetTermId = queryStringValues.targetTermId ? parseInt(queryStringValues.targetTermId, 10) : null;
    const allowUnknownGrade = queryStringValues.allowUnknownGrade === 'true' ? true : false;
    const minGrade = queryStringValues.minGrade ? parseInt(queryStringValues.minGrade, 10) : null;
    const maxGrade = queryStringValues.maxGrade ? parseInt(queryStringValues.maxGrade, 10) : null;

    // Set specification to initial value and term
    store.dispatch(AppActions.setSpecification(specificationTermId, queryStringValues.specification));

    // set target term
    store.dispatch(AppActions.setTargetTermId(targetTermId));

    // set allow unknown grade
    store.dispatch(AppActions.setAllowUnknownGrade(allowUnknownGrade));

    // set max grade
    store.dispatch(AppActions.setMaxGrade(maxGrade));

    // set min grade
    store.dispatch(AppActions.setMinGrade(minGrade));
}

/*
 * ---------------------------------------------------------------------------------
 * Script
 * ---------------------------------------------------------------------------------
 */

initialiseAppState();

// Render application to HTML DOM.
ReactDOM.render(
    <Provider store={store}>
        <MessageManagerContext.Provider
            value={messageManager}
        >
            <ThemeManagerContext.Provider
                value={themeManager}
            >
                <ThemeProvider>
                    <BrowserRouter>
                        <MessageProvider />
                        <Switch>
                            <Route path="/error/:errorCode" component={Errors} />
                            <Route path="/:versionCode" component={App} />
                            <Redirect path="/" to="/error/404" />
                        </Switch>
                    </BrowserRouter>
                </ThemeProvider>
            </ThemeManagerContext.Provider>
        </MessageManagerContext.Provider>
    </Provider>,
    document.getElementById('root') as HTMLElement
);

// Register or Update service worker.
serviceWorker.unregister();


// WEBPACK FOOTER //
// ./src/index.tsx